class FeaturedCollectionCarousel extends HTMLElement {
  #currentIndex = 0;
  #isAnimating = false;
  #slides = [];
  #track = null;
  #viewport = null;

  connectedCallback() {
    this.#track = this.querySelector('.fcc__track');
    this.#viewport = this.querySelector('.fcc__viewport');
    if (!this.#track || !this.#viewport) return;

    this.#slides = [...this.#track.querySelectorAll('.fcc__slide:not([data-clone])')];
    if (this.#slides.length < 1) return;

    if (this.#slides.length > 1) this.#createClones();

    // Suppress CSS width transitions during initial setup so #lockHeight
    // measures the fully-settled active slide, not a mid-transition state.
    const allSlides = [...this.#track.querySelectorAll('.fcc__slide')];
    allSlides.forEach((s) => { s.style.transition = 'none'; });
    this.#render(false);
    void this.#track.offsetWidth;
    this.#lockHeight();
    void this.#track.offsetWidth;
    allSlides.forEach((s) => { s.style.transition = ''; });

    this.querySelector('.fcc__btn--prev')?.addEventListener('click', () => this.#navigate(-1));
    this.querySelector('.fcc__btn--next')?.addEventListener('click', () => this.#navigate(1));

    this.#track.addEventListener('transitionend', (e) => {
      if (e.target === this.#track && e.propertyName === 'transform') {
        this.#handleTransitionEnd();
      }
    });

    // Click an inactive slide's media to bring it to center
    this.#track.addEventListener('click', (e) => {
      const slide = e.target.closest('.fcc__slide');
      if (!slide || this.#isAnimating) return;
      if (slide.dataset.clone) return;
      const clickedIndex = this.#slides.indexOf(slide);
      if (clickedIndex === -1 || clickedIndex === this.#currentIndex) return;
      if (!e.target.closest('.fcc__slide-media')) return;
      const steps = clickedIndex - this.#currentIndex;
      this.#navigate(steps > 0 ? 1 : -1);
    });

    // Active slide click → go to product
    this.#track.addEventListener('click', (e) => {
      const slide = e.target.closest('.fcc__slide--active:not([data-clone])');
      if (!slide) return;
      const url = slide.dataset.url;
      if (url) window.location.href = url;
    });

    window.addEventListener('resize', () => {
      this.#unlockHeight();
      const resizeSlides = [...this.#track.querySelectorAll('.fcc__slide')];
      resizeSlides.forEach((s) => { s.style.transition = 'none'; });
      this.#render(false);
      void this.#track.offsetWidth;
      this.#lockHeight();
      void this.#track.offsetWidth;
      resizeSlides.forEach((s) => { s.style.transition = ''; });
    });
  }

  /** Prepend 2 clones of the last 2 slides; append 2 clones of the first 2. */
  #createClones() {
    const n = this.#slides.length;
    const cloneCount = Math.min(2, n);

    // Prepend clones of last N slides (in order: clone[N-2], clone[N-1])
    const prependFragment = document.createDocumentFragment();
    for (let i = cloneCount - 1; i >= 0; i--) {
      const clone = this.#slides[n - 1 - i].cloneNode(true);
      clone.setAttribute('data-clone', '');
      clone.removeAttribute('id');
      prependFragment.appendChild(clone);
    }
    this.#track.insertBefore(prependFragment, this.#track.firstChild);

    // Append clones of first N slides
    for (let i = 0; i < cloneCount; i++) {
      const clone = this.#slides[i].cloneNode(true);
      clone.setAttribute('data-clone', '');
      clone.removeAttribute('id');
      this.#track.appendChild(clone);
    }
  }

  /** Position in the full track (clones + real slides). Real slide 0 is at offset 2. */
  get #trackIndex() {
    return this.#slides.length > 1 ? this.#currentIndex + 2 : 0;
  }

  #render(animate) {
    const W = this.#viewport.clientWidth;

    // Toggle active on real slides
    this.#slides.forEach((slide, i) => {
      slide.classList.toggle('fcc__slide--active', i === this.#currentIndex);
    });

    // Clear active from all clones, then mark the destination clone active if applicable
    const allSlides = [...this.#track.querySelectorAll('.fcc__slide')];
    allSlides.forEach((s) => {
      if (s.hasAttribute('data-clone')) s.classList.remove('fcc__slide--active');
    });
    const ti = this.#trackIndex;
    const destSlide = allSlides[ti];
    if (destSlide?.hasAttribute('data-clone')) {
      destSlide.classList.add('fcc__slide--active');
    }

    // Read percentages from CSS custom properties so JS stays in sync with responsive CSS.
    const cs = getComputedStyle(this);
    const activeW = W * (parseFloat(cs.getPropertyValue('--fcc-active-pct')) || 50) / 100;
    const inactiveW = W * (parseFloat(cs.getPropertyValue('--fcc-inactive-pct')) || 40) / 100;
    const translateX = W / 2 - (ti * inactiveW + activeW / 2);

    if (animate) {
      this.#track.style.transition = 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    } else {
      this.#track.style.transition = 'none';
      void this.#track.offsetWidth;
    }
    this.#track.style.transform = `translateX(${translateX}px)`;
  }

  #navigate(direction) {
    if (this.#isAnimating) return;
    this.#isAnimating = true;
    this.#currentIndex += direction;
    this.#render(true);
  }

  #handleTransitionEnd() {
    const n = this.#slides.length;
    const needsWrap = this.#currentIndex < 0 || this.#currentIndex >= n;

    if (this.#currentIndex < 0) {
      this.#currentIndex = ((this.#currentIndex % n) + n) % n;
    } else if (this.#currentIndex >= n) {
      this.#currentIndex = this.#currentIndex % n;
    }

    if (needsWrap) {
      // Suppress CSS width transitions so the teleport back to the real slide is instant
      const allSlides = [...this.#track.querySelectorAll('.fcc__slide')];
      allSlides.forEach((s) => { s.style.transition = 'none'; });
      void this.#track.offsetWidth;
      this.#render(false);
      void this.#track.offsetWidth;
      allSlides.forEach((s) => { s.style.transition = ''; });
    }

    this.#isAnimating = false;
  }

  #lockHeight() {
    const area = this.querySelector('.fcc__carousel-area');
    if (area) area.style.height = area.offsetHeight + 'px';
  }

  #unlockHeight() {
    const area = this.querySelector('.fcc__carousel-area');
    if (area) area.style.height = '';
  }
}

if (!customElements.get('featured-collection-carousel')) {
  customElements.define('featured-collection-carousel', FeaturedCollectionCarousel);
}
